import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material';
import InvoiceCategoryTabs from '../../../components/Billing/Invoice/InvoiceCategoryTabs';
import SearchBar from '../../../components/Billing/Invoice/InvoiceSearchBar';
import InvoiceFilter, { InvoiceFilterValues } from '../../../components/Billing/Invoice/InvoiceFilter';
import { ReactComponent as FunnelIcon } from '../../../assets/SVG/Filter Icon Box.svg';
import RefundRequestList from './RefundRequestList';
import RefundedList from './RefundedList';
import PharmacyRefundList from './PharmacyRefundList';
import { useAppQuery } from '../../../services/appQuery';
import { getRefundStats, RefundStats } from './Refund.helper';
import { getRequest } from '../../../services/httpService';

type RefundTabKey = 'REFUND_REQUEST' | 'REFUNDED' | 'PHARMACY_REFUND';

const Refund: React.FC = () => {
  const [activeTab, setActiveTab] = useState<RefundTabKey>('REFUND_REQUEST');
  const [search, setSearch] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState<InvoiceFilterValues>({ fromDate: '', toDate: '', billType: '' });

  const { data: refundStats } = useAppQuery<RefundStats>(
    ['RefundStats'],
    () => getRequest<RefundStats>(getRefundStats()),
    { staleTime: 0, gcTime: 0, enabled: true, refetchOnMount: true }
  );
      
  const { t: tBilling } = useTranslation('billing_config');
  const categories = useMemo(
    () => [
      { name: 'REFUND_REQUEST', label: tBilling('Refund.refund_request', 'Refund Request'), count: refundStats?.refund_requested || 0 },
      { name: 'REFUNDED', label: tBilling('Refund.refunded', 'Refunded'), count: refundStats?.refunded || 0 },
      { name: 'PHARMACY_REFUND', label: tBilling('Refund.pharmacy_refund', 'Pharmacy Refund'), count: refundStats?.pharmacy_return || 0 },
    ],
    [refundStats, tBilling]
  );


  const renderTabContent = () => {
    switch (activeTab) {
    case 'REFUND_REQUEST':
      return <RefundRequestList search={search} filters={filters} />;
    case 'REFUNDED':
      return <RefundedList search={search} filters={filters} />;
    case 'PHARMACY_REFUND':
      return <PharmacyRefundList search={search} filters={filters} />;
    default:
      return null;
    }
  };

  return (
    <Box sx={{ px: 3, py: 2 }} data-testid="refund-page-container">
      {/* Header: Tabs left, filter + search right (same pattern as Advance) */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 2 }} data-testid="refund-header-section">
        <InvoiceCategoryTabs
          activeCategory={activeTab}
          categories={categories}
          onCategoryChange={(cat) => setActiveTab(cat.name as RefundTabKey)}
        />

        <Stack direction="row" spacing={1.5} alignItems="center">
          <Tooltip title={tBilling('invoice.filter.title', 'Filters')} arrow>
            <IconButton aria-label={tBilling('Refund.common.openFiltersAria', 'Open filters')} onClick={() => setIsFilterOpen(true)} sx={{ width: 40, height: 40, p: 0 }}>
              <FunnelIcon style={{ width: 40, height: 40 }} />
            </IconButton>
          </Tooltip>
          <SearchBar
            height={40}
            value={search}
            onChange={setSearch}
            placeholder={tBilling('Refund.search_by_patient_name', 'Search By Patient Name')}
            minWidth={420}
          />
        </Stack>
      </Stack>
      {/* Content area for the active tab */}
      {renderTabContent()}
      {/* Shared filter modal */}
      <InvoiceFilter
        open={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onApply={(vals) => { setFilters(vals); setIsFilterOpen(false); }}
        showBillType={false}
        initialValues={filters}
      />

    </Box>
  );
};

export default Refund;
